import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import { styled } from 'styled-components';
import 'quill/dist/quill.snow.css';

export type QuillEditorProps = {
  value?: string;
  placeholder?: string;
  onChange: (html: string) => void;
};

const EditorHost = styled.div`
  .ql-toolbar.ql-snow {
    border-radius: 6px 6px 0 0;
  }

  .ql-container.ql-snow {
    border-radius: 0 0 6px 6px;
    font-size: 14px;
  }

  .ql-editor {
    min-height: 160px;
  }
`;

export const QuillEditor: React.FC<QuillEditorProps> = ({
  value = '',
  placeholder,
  onChange,
}) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  const initialValueRef = useRef(value);
  const placeholderRef = useRef(placeholder);
  onChangeRef.current = onChange;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const editor = host.ownerDocument.createElement('div');
    host.appendChild(editor);

    const quill = new Quill(editor, {
      theme: 'snow',
      placeholder: placeholderRef.current,
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
        ],
      },
    });

    if (initialValueRef.current) {
      quill.clipboard.dangerouslyPasteHTML(initialValueRef.current);
    }

    quill.on(Quill.events.TEXT_CHANGE, () => {
      onChangeRef.current(quill.root.innerHTML);
    });

    return () => {
      host.replaceChildren();
    };
  }, []);

  return <EditorHost ref={hostRef} />;
};
